import {
	BULLET_PREFIX,
	parseProntuarioNotesDocument,
	type ProntuarioNotesSegment,
} from './parseProntuarioNotesSegments';

export type ProntuarioNotesCursor = {
	start: number;
	end: number;
};

export type ProntuarioNotesRenderChunk =
	| { kind: 'plain'; text: string }
	| { kind: 'delim'; text: string }
	| { kind: 'bold' | 'italic' | 'code'; text: string };

export type ProntuarioNotesDisplayModel = {
	display: string;
	displayToSource: number[];
	/** Índice source do início de cada caractere visível. */
	displayCharSourceStart: number[];
	/** Índice source exclusivo do fim de cada caractere visível. */
	displayCharSourceEnd: number[];
	sourceToDisplay: number[];
	chunks: ProntuarioNotesRenderChunk[];
};

const isFormatSegment = (
	segment: ProntuarioNotesSegment,
): segment is Extract<
	ProntuarioNotesSegment,
	{ type: 'bold' | 'italic' | 'code' }
> => segment.type !== 'plain';

const segmentIsActive = (
	segment: ProntuarioNotesSegment,
	cursor: ProntuarioNotesCursor,
): boolean => {
	if (segment.type === 'plain') return false;
	return (
		(cursor.start >= segment.start && cursor.start <= segment.end) ||
		(cursor.end >= segment.start && cursor.end <= segment.end) ||
		(cursor.start < segment.end && cursor.end > segment.start)
	);
};

export const buildProntuarioNotesDisplayModel = (
	source: string,
	cursor: ProntuarioNotesCursor,
): ProntuarioNotesDisplayModel => {
	const displayToSource: number[] = [0];
	const displayCharSourceStart: number[] = [];
	const displayCharSourceEnd: number[] = [];
	const sourceToDisplay = new Array<number>(source.length + 1).fill(0);
	const chunks: ProntuarioNotesRenderChunk[] = [];
	let display = '';

	const markSource = (sourceIndex: number, displayIndex: number) => {
		if (sourceIndex >= 0 && sourceIndex <= source.length) {
			sourceToDisplay[sourceIndex] = displayIndex;
		}
	};

	const appendChar = (char: string, sourceIndex: number) => {
		markSource(sourceIndex, display.length);
		displayCharSourceStart.push(sourceIndex);
		display += char;
		displayCharSourceEnd.push(sourceIndex + 1);
		displayToSource.push(sourceIndex + 1);
		markSource(sourceIndex + 1, display.length);
	};

	const appendText = (
		text: string,
		sourceStart: number,
		chunk: ProntuarioNotesRenderChunk,
	) => {
		if (!text) return;

		for (let index = 0; index < text.length; index += 1) {
			appendChar(text[index], sourceStart + index);
		}

		chunks.push(chunk);
	};

	const appendPlain = (text: string, sourceStart: number) => {
		appendText(text, sourceStart, { kind: 'plain', text });
	};

	const appendFormatted = (
		segment: Extract<
			ProntuarioNotesSegment,
			{ type: 'bold' | 'italic' | 'code' }
		>,
		active: boolean,
	) => {
		const innerStart = segment.start + segment.open.length;
		const innerEnd = segment.end - segment.close.length;

		if (active) {
			appendText(segment.open, segment.start, {
				kind: 'delim',
				text: segment.open,
			});
			appendText(segment.inner, innerStart, {
				kind: segment.type,
				text: segment.inner,
			});
			appendText(segment.close, innerEnd, {
				kind: 'delim',
				text: segment.close,
			});
			return;
		}

		const displayStart = display.length;
		for (
			let sourceIndex = segment.start;
			sourceIndex < innerStart;
			sourceIndex += 1
		) {
			markSource(sourceIndex, displayStart);
		}

		appendText(segment.inner, innerStart, {
			kind: segment.type,
			text: segment.inner,
		});

		const displayEnd = display.length;
		for (
			let sourceIndex = innerEnd;
			sourceIndex <= segment.end;
			sourceIndex += 1
		) {
			markSource(sourceIndex, displayEnd);
		}

		// Cursor/inserção após o trecho colapsado ignora delimitadores ocultos.
		displayToSource[displayEnd] = segment.end;
	};

	const parsed = parseProntuarioNotesDocument(source);

	for (let lineIndex = 0; lineIndex < parsed.length; lineIndex += 1) {
		const entry = parsed[lineIndex];

		if (lineIndex > 0) {
			const newlineIndex = entry.lineOffset - 1;
			markSource(newlineIndex, display.length);
			appendChar('\n', newlineIndex);
			chunks.push({ kind: 'plain', text: '\n' });
		}

		if (entry.bulletPrefix) {
			appendPlain(BULLET_PREFIX, entry.lineOffset);
		}

		for (const segment of entry.segments) {
			if (segment.type === 'plain') {
				appendPlain(segment.text, segment.start);
				continue;
			}

			appendFormatted(segment, segmentIsActive(segment, cursor));
		}
	}

	markSource(source.length, display.length);

	return {
		display,
		displayToSource,
		displayCharSourceStart,
		displayCharSourceEnd,
		sourceToDisplay,
		chunks,
	};
};

/** Inverte displayToSource (monotônico) — evita buracos no mapa reverso. */
export const sourceOffsetToDisplay = (
	model: ProntuarioNotesDisplayModel,
	sourceOffset: number,
): number => {
	const positions = model.displayToSource;
	if (positions.length === 0) return 0;

	const target = Math.max(0, sourceOffset);
	let lo = 0;
	let hi = positions.length - 1;

	while (lo < hi) {
		const mid = Math.ceil((lo + hi) / 2);
		if (positions[mid] <= target) lo = mid;
		else hi = mid - 1;
	}

	return lo;
};

export const displayOffsetToSource = (
	model: ProntuarioNotesDisplayModel,
	displayOffset: number,
): number => {
	const clamped = Math.max(
		0,
		Math.min(displayOffset, model.displayToSource.length - 1),
	);
	return model.displayToSource[clamped] ?? 0;
};

export const applyProntuarioNotesDisplayChange = (
	source: string,
	model: ProntuarioNotesDisplayModel,
	nextDisplay: string,
): { source: string; cursor: ProntuarioNotesCursor } => {
	const oldDisplay = model.display;

	if (oldDisplay === nextDisplay) {
		return {
			source,
			cursor: {
				start: displayOffsetToSource(model, oldDisplay.length),
				end: displayOffsetToSource(model, oldDisplay.length),
			},
		};
	}

	let prefix = 0;
	while (
		prefix < oldDisplay.length &&
		prefix < nextDisplay.length &&
		oldDisplay[prefix] === nextDisplay[prefix]
	) {
		prefix += 1;
	}

	let suffix = 0;
	while (
		suffix < oldDisplay.length - prefix &&
		suffix < nextDisplay.length - prefix &&
		oldDisplay[oldDisplay.length - 1 - suffix] ===
			nextDisplay[nextDisplay.length - 1 - suffix]
	) {
		suffix += 1;
	}

	const changeStart = prefix;
	const changeEnd = oldDisplay.length - suffix;

	let sourceStart: number;
	let sourceEnd: number;

	if (changeStart < changeEnd) {
		sourceStart = model.displayCharSourceStart[changeStart] ?? 0;
		sourceEnd =
			model.displayCharSourceEnd[changeEnd - 1] ??
			displayOffsetToSource(model, changeEnd);
	} else {
		sourceStart =
			changeStart < oldDisplay.length
				? model.displayCharSourceStart[changeStart] ??
				  displayOffsetToSource(model, changeStart)
				: displayOffsetToSource(model, changeStart);
		sourceEnd = sourceStart;
	}

	const inserted = nextDisplay.slice(prefix, nextDisplay.length - suffix);
	const nextSource =
		source.slice(0, sourceStart) + inserted + source.slice(sourceEnd);
	const cursor = sourceStart + inserted.length;

	return {
		source: nextSource,
		cursor: { start: cursor, end: cursor },
	};
};

export const displaySelectionNeedsRefresh = (
	source: string,
	previous: ProntuarioNotesCursor,
	next: ProntuarioNotesCursor,
): boolean => {
	if (previous.start === next.start && previous.end === next.end) {
		return false;
	}

	const parsed = parseProntuarioNotesDocument(source);

	for (const entry of parsed) {
		for (const segment of entry.segments) {
			if (!isFormatSegment(segment)) continue;

			const wasActive = segmentIsActive(segment, previous);
			const isActive = segmentIsActive(segment, next);
			if (wasActive !== isActive) return true;
		}
	}

	return false;
};
